// Payment and subscription management for Square integration

import { requireLogin, getToken } from './auth.js';

export async function startSubscription() {
  const user = requireLogin();
  if (!user) return;

  try {
    const token = await getToken();
    
    // 1) Create or fetch Square customer for this Identity user
    const customerRes = await fetch('/.netlify/functions/create-checkout-customer', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        Authorization: `Bearer ${token}` 
      },
      body: JSON.stringify({})
    });
    
    if (!customerRes.ok) {
      throw new Error('Failed to create customer');
    }
    
    const { customerId } = await customerRes.json();

    // 2) Initialize Square Web Payments SDK
    if (!window.Square) {
      throw new Error('Square Web Payments SDK not loaded');
    }

    // Get Square configuration securely from server
    const configRes = await fetch('/.netlify/functions/square-config');
    if (!configRes.ok) {
      throw new Error('Failed to load Square configuration');
    }
    
    const { appId: squareAppId, locationId: squareLocationId } = await configRes.json();
    
    const payments = window.Square.payments(squareAppId, locationId);
    
    const card = await payments.card();
    await card.attach('#card-container');

    // 3) Handle card submission
    const submitButton = document.querySelector('#card-submit');
    if (submitButton) {
      submitButton.onclick = async () => {
      try {
        const result = await card.tokenize();
        if (result.status !== 'OK') { 
          alert('Card error: ' + result.status); 
          return; 
        }

        // 4) Store card on file
        const saveRes = await fetch('/.netlify/functions/square-store-card', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ customerId, sourceId: result.token })
        });
        
        if (!saveRes.ok) {
          throw new Error('Failed to store card');
        }
        
        const { cardId } = await saveRes.json();

        // 5) Create subscription
        const subRes = await fetch('/.netlify/functions/square-create-subscription', {
          method: 'POST',
          headers: { 
            'Content-Type': 'application/json', 
            Authorization: `Bearer ${token}` 
          },
          body: JSON.stringify({ customerId, cardId })
        });
        
        if (!subRes.ok) {
          throw new Error('Failed to create subscription');
        }
        
        const { status } = await subRes.json();
        
        if (status === 'active' || status === 'pending') {
          alert('Subscription created successfully!');
          window.location.href = '/dashboard';
        } else {
          alert('Subscription not active yet. Status: ' + status);
        }
      } catch (error) {
        console.error('Subscription error:', error);
        alert('Error creating subscription: ' + error.message);
              }
      };
    }

  } catch (error) {
    console.error('Subscription setup error:', error);
    alert('Error setting up subscription: ' + error.message);
  }
}

export async function cancelSubscription() {
  const user = requireLogin();
  if (!user) return;

  try {
    const token = await getToken();
    
    const res = await fetch('/.netlify/functions/square-cancel-subscription', {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json', 
        Authorization: `Bearer ${token}` 
      }
    });
    
    if (res.ok) {
      alert('Subscription cancelled successfully');
      window.location.reload();
    } else {
      throw new Error('Failed to cancel subscription');
    }
  } catch (error) {
    console.error('Cancel error:', error);
    alert('Error cancelling subscription: ' + error.message);
  }
}

export function getSubscriptionStatus() {
  const user = netlifyIdentity.currentUser();
  if (!user) return null;
  
  const meta = user.app_metadata || {};
  const now = Math.floor(Date.now() / 1000);
  
  return {
    active: !!meta.active,
    notExpired: !meta.current_period_end || meta.current_period_end > now,
    plan: meta.plan || 'free',
    subscriptionId: meta.subscription_id
  };
}
